import { Input, InputNumber } from 'antd'
import type { InputProps } from 'antd/es/input'
import type { InputNumberProps } from 'antd/es/input-number'
import type { PasswordProps } from 'antd/es/input/Password'
import type { SearchProps } from 'antd/es/input/Search'
import type { TextAreaProps } from 'antd/es/input/TextArea'
import type React from 'react'
import { forwardRef } from 'react'
import './UiInput.scss'

type InputType = 'textarea' | 'number' | 'search' | 'password' | 'otp' | 'default'

type CommonProps = {
  label?: string
  type?: InputType
  floating?: boolean
  className?: string
  /** Điểm bám cho e2e. Không truyền thì không render attribute nào. */
  testId?: string
  /** Icon nằm sát trái ô input — caller quyết định icon nào, UiInput chỉ lo layout/UX */
  prefixIcon?: React.ReactNode
  /** Icon nằm sát phải (hiếm dùng, ưu tiên prefix cho UX) */
  suffixIcon?: React.ReactNode
}

type InputCustomProps =
  | (CommonProps & InputProps & { type?: 'default' | undefined })
  | (CommonProps & TextAreaProps & { type: 'textarea' })
  | (CommonProps & PasswordProps & { type: 'password' })
  | (CommonProps & SearchProps & { type: 'search' })
  | (CommonProps & InputNumberProps & { type: 'number' })
  | (CommonProps & Record<string, unknown> & { type: 'otp' })

const UiInput = forwardRef<React.ElementRef<typeof Input>, InputCustomProps>((props, ref) => {
  const {
    label = '',
    type = 'default',
    floating = false,
    className = '',
    testId,
    prefixIcon,
    suffixIcon,
    ...restProps
  } = props
  const rest: Record<string, any> = {
    ...(restProps as Record<string, any>),
    'data-testid': testId,
  }

  // caller quyết định icon trái — UiInput chỉ bọc layout/UX
  if (prefixIcon && !('prefix' in rest)) {
    ;(rest as any).prefix = <span className="ui-input-prefix">{prefixIcon as React.ReactNode}</span>
  }
  if (suffixIcon && !('suffix' in rest)) {
    ;(rest as any).suffix = suffixIcon as React.ReactNode
  }

  const typeMap: Record<string, unknown> = {
    textarea: Input.TextArea,
    number: InputNumber,
    search: Input.Search,
    password: Input.Password,
    otp: (Input as unknown as Record<string, React.ElementType>).OTP, // nếu có
    default: Input,
  }

  const Component = (typeMap[type as string] || Input) as React.ElementType
  let style: React.CSSProperties = (rest.style as React.CSSProperties) || {}

  if (type === 'textarea') {
    style = {
      ...style,
      minHeight: '80px',
      overflowX: 'hidden',
      overflowY: 'auto', // hoặc 'hidden' tùy nhu cầu
    }
  } else if (type === 'number') {
    style = { ...style, width: '100%' }
  }

  // ưu tiên làm đẹp: luôn gắn ui-input để SCSS beautified được áp
  const beautifiedClass = 'ui-input'
  return (
    // chuyển tiếp ref xuống component antd tương ứng
    <Component
      ref={ref}
      placeholder={label}
      className={`${beautifiedClass} ${floating ? 'floating-label' : ''} ${className}`}
      {...(type === 'search' ? { enterButton: true } : {})}
      {...rest}
      style={style}
    />
  )
})

UiInput.displayName = 'UiInput'

const UiInputWithSub = UiInput as typeof UiInput & {
  TextArea: typeof Input.TextArea
  Search: typeof Input.Search
  Password: typeof Input.Password
  OTP: typeof Input.OTP
}
UiInputWithSub.TextArea = Input.TextArea
UiInputWithSub.Search = Input.Search
UiInputWithSub.Password = Input.Password
UiInputWithSub.OTP = Input.OTP

export default UiInputWithSub
